import { useState } from "react";
import { useParams, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import {
  Pencil,
  Trash2,
  Loader2,
  AlertCircle,
  Printer,
  Box,
  Package,
  CheckCircle2,
  ShieldCheck,
  Camera,
  Bug,
  Truck,
  Clock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { trpcClient } from "@/lib/trpc";
import { formatDate, formatNumber } from "@/lib/utils";
import StandardDetailLayout from "@/layouts/StandardDetailLayout";
import { getStatusConfig } from "@/config/status-registry";
import WorkflowStepper from "@/components/shared/WorkflowStepper";
import { useWorkflowSteps } from "@/hooks/useWorkflowSteps";
import { EntityLink } from "@/components/shared/EntityLink";
import PrintDialog from "@/components/shared/PrintDialog";
import DetailNotFoundState from "@/components/shared/DetailNotFoundState";
import {
  StageDefectSelectorTable,
  normalizeDefectTallyItems,
} from "@/components/quality/StageDefectSelectorTable";
import {
  CriteriaTable,
  LegacyMeasurements,
  QcSamplingSummary,
  VerdictSummary,
  checklistItemsOf,
  legacyBarcodeLabel,
  legacyTestLabel,
} from "@/components/quality/qc-form";
import {
  QcCategorizedMediaSection,
  normalizeCategorizedAttachments,
} from "@/components/quality/QcCategorizedMediaSection";
import { DetailField as F } from "@/components/shared/DetailField";



export default function QCFinalDetailPage() {
  const navigate = useNavigate();
  const { checkId } = useParams({ strict: false }) as { checkId: string };
  const [showDelete, setShowDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showFqcPrint, setShowFqcPrint] = useState(false);
  const [showCoaPrint, setShowCoaPrint] = useState(false);

  const queryClient = useQueryClient();
  const { workflow } = useWorkflowSteps("qcFinal");

  const {
    data: qc,
    isLoading,
    error,
    refetch: refetchDetail,
  } = useQuery({
    queryKey: ["qcFinal", checkId],
    queryFn: async () =>
      (trpcClient as any).qcFinal.getById.query({ id: checkId }),
  });

  const d = qc as any;
  // Lệnh SX nếu có; lô gia công 100% lấy sản phẩm / khách / đơn hàng của chính biên bản
  const srcProduct = d?.productionOrder?.product || d?.product || null;
  const srcCustomer = d?.productionOrder?.customer || d?.customer || null;
  const srcOrder = d?.productionOrder?.salesOrder || d?.order || null;

  const updateStatusMutation = useMutation({
    mutationFn: async (newStatus: string) => {
      return (trpcClient as any).qcFinal.updateStatus.mutate({
        id: checkId,
        status: newStatus,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["qcFinal", checkId] });
      queryClient.invalidateQueries({ queryKey: ["qcFinal"] });
      queryClient.invalidateQueries({ queryKey: ["inventory"] });
    },
    onError: (err: any) => {
      toast.error(err.message || "Có lỗi xảy ra khi cập nhật trạng thái");
    },
  });

  const handleUpdateStatus = async (newStatus: string) => {
    await updateStatusMutation.mutateAsync(newStatus);
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await (trpcClient as any).qcFinal.delete.mutate({ id: checkId });
      navigate({ to: "/qc-final" });
    } catch (err: any) {
      toast.error(err.message || "Lỗi khi xóa");
    } finally {
      setIsDeleting(false);
      setShowDelete(false);
    }
  };

  // Gõ/dán URL có id sai (404) hoặc không tải được: không hiển thị khung tải vô hạn
  if (!isLoading && !d) {
    return (
      <DetailNotFoundState
        breadcrumbs={[{ label: "QC Cuối", to: "/qc-final" }]}
        entityLabel="Phiếu QC cuối"
        backTo="/qc-final"
        codeOrId={checkId}
        error={error}
        onRetry={() => refetchDetail()}
      />
    );
  }
  if (isLoading || !d) {
    return (
      <StandardDetailLayout
        breadcrumbs={[{ label: "QC Cuối", to: "/qc-final" }]}
        title="Đang tải..."
      >
        <div className="flex items-center justify-center h-48">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
        </div>
      </StandardDetailLayout>
    );
  }

  if (error) {
    return (
      <StandardDetailLayout
        breadcrumbs={[{ label: "QC Cuối", to: "/qc-final" }]}
        title="Lỗi"
      >
        <div className="p-8 text-center">
          <AlertCircle className="h-12 w-12 text-rose-500 mx-auto mb-4" />
          <Button onClick={() => navigate({ to: "/qc-final" })}>
            Quay lại
          </Button>
        </div>
      </StandardDetailLayout>
    );
  }

  const sts = getStatusConfig("QCStatus", d.status);
  const checklistItems: any[] = checklistItemsOf(d.checklist);

  return (
    <>
      <StandardDetailLayout
        breadcrumbs={[{ label: "QC Cuối", to: "/qc-final" }]}
        title={d.finalQcId}
        entityId={d.id}
        entityType="qc_final"
        timestamps={{ created: d.createdAt, updated: d.updatedAt }}
        status={
          <Badge variant="outline" className={sts.color}>
            {sts.label || d.status}
          </Badge>
        }
        actions={
          <div className="flex items-center gap-1.5 flex-wrap">
            {(d.status === "PASSED" || d.status === "WARNING") && (
              <Button
                variant="default"
                size="sm"
                title={`Phát hành / In CoA (${d.coaNo || "BM-QA-COA-01"})`}
                className="gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold shadow-xs"
                onClick={() => setShowCoaPrint(true)}
              >
                <ShieldCheck size={14} /> In CoA
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              title="In biên bản FQC (BM-QC-03)"
              className="gap-1 text-indigo-700 border-indigo-200 hover:bg-indigo-50 font-semibold"
              onClick={() => setShowFqcPrint(true)}
            >
              <Printer size={14} /> In biên bản
            </Button>
            {/* Đóng gói, in tem thùng / pallet, nhập kho, giao hàng làm trên Phiếu đóng gói (Sản xuất) */}
            {d.packingSlip && (
              <Button
                size="sm"
                variant="outline"
                data-testid="btn-open-packing-slip"
                className="gap-1 border-amber-300 text-amber-900 hover:bg-amber-50 font-semibold"
                onClick={() => navigate({ to: "/factory/packing", search: { q: d.packingSlip.code } as any })}
                title="Mở phiếu đóng gói ở Xưởng sản xuất › Đóng gói & Nhập kho"
              >
                <Box size={14} /> {d.packingSlip.code}
              </Button>
            )}
            {(d.status === "FAILED" || d.status === "WARNING" || (d.qtyFailed || 0) > 0) && (
              <Button
                size="sm"
                data-testid="btn-create-ncr-from-fqc"
                className="bg-rose-600 hover:bg-rose-700 text-white font-bold gap-1.5 shadow-xs"
                onClick={() =>
                  navigate({
                    to: "/ncr/create",
                    search: { sourceType: "FQC", sourceId: d.id } as any,
                  })
                }
              >
                <Bug size={14} /> Tạo NCR
              </Button>
            )}
            <Button
              variant="default"
              size="sm"
              onClick={() =>
                navigate({
                  to: "/qc-final/$checkId/edit",
                  params: { checkId },
                })
              }
            >
              <Pencil size={14} className="mr-1.5" /> Sửa
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="text-rose-600 border-rose-200 hover:bg-rose-50"
              onClick={() => setShowDelete(true)}
            >
              <Trash2 size={14} className="mr-1.5" /> Xoá
            </Button>
          </div>
        }
        onBack={() => navigate({ to: "/qc-final" })}
        workflow={
          <WorkflowStepper
            workflow={workflow}
            currentStatus={d.status}
            onStatusChange={handleUpdateStatus}
            disabled={updateStatusMutation.isPending}
          />
        }
      >
        <div className="space-y-4">
          {/* Rule 8: Alert Banner for FAILED / WARNING FQC with Link-to-Form NCR */}
          {(d.status === "FAILED" || d.status === "WARNING") && (
            <div className="border-2 border-rose-300 bg-rose-50/90 rounded-lg p-3.5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-start gap-2.5">
                <AlertCircle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
                <div>
                  <div className="text-xs font-black uppercase text-rose-900">
                    {d.status === "FAILED"
                      ? "Lô thành phẩm Không đạt — đã khóa xuất giao"
                      : "Lô thành phẩm Nhân nhượng"}
                  </div>
                  <p className="text-xs text-rose-700 mt-0.5">
                    Ghi nhận {formatNumber(d.qtyFailed || 0)} sản phẩm lỗi. Bắt buộc lập Phiếu sự cố chất lượng (NCR 8D) để truy vết nguyên nhân 6M/5-Why và phê duyệt hướng xử lý.
                  </p>
                </div>
              </div>
              <Button
                size="sm"
                className="bg-rose-600 hover:bg-rose-700 text-white font-bold shrink-0 gap-1.5"
                onClick={() =>
                  navigate({
                    to: "/ncr/create",
                    search: { sourceType: "FQC", sourceId: d.id } as any,
                  })
                }
              >
                <Bug className="w-3.5 h-3.5" /> Tạo NCR
              </Button>
            </div>
          )}

          {/* Thông tin chung + Lấy mẫu */}
          <div className="border rounded-lg bg-white shadow-2xs p-4 space-y-3">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-800 border-b border-slate-100 pb-2">
              Thông tin chung
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-2">
              {/* Lô gia công 100% không có lệnh SX: nguồn là đơn gia công (04/11/2026) */}
              {!d.productionOrder && d.outsourcePo ? (
                <F
                  label="Đơn gia công"
                  value={<EntityLink entityType="outsourceOrder" id={d.outsourcePo.id} code={d.outsourcePo.poId} variant="badge" />}
                />
              ) : (
                <F
                  label="Lệnh SX"
                  value={
                    <EntityLink
                      entityType="productionOrder"
                      id={d.productionOrder?.orderId || d.productionOrderId}
                      code={d.productionOrder?.orderId}
                      label={d.productionOrder?.productName}
                      variant="badge"
                    />
                  }
                />
              )}
              <F
                label="Sản phẩm"
                value={
                  srcProduct ? (
                    <EntityLink
                      entityType="product"
                      id={srcProduct.id}
                      code={srcProduct.productCode}
                      label={srcProduct.productName || d.productName}
                    />
                  ) : (
                    d.productName || "—"
                  )
                }
              />
              <F
                label="Khách hàng"
                value={
                  srcCustomer ? (
                    <EntityLink
                      entityType="customer"
                      id={srcCustomer.id}
                      code={srcCustomer.code}
                      label={srcCustomer.shortName || srcCustomer.name}
                    />
                  ) : (
                    "—"
                  )
                }
              />
              <F
                label="Đơn hàng"
                value={
                  srcOrder ? (
                    <EntityLink
                      entityType="order"
                      id={srcOrder.orderId || srcOrder.id}
                      code={srcOrder.orderId}
                      variant="badge"
                    />
                  ) : (
                    "—"
                  )
                }
              />
              <F label="Loại SP" value={d.productType?.code || "—"} mono />
              <F label="Mẫu chuẩn" value={d.referenceSample || "—"} hl={Boolean(d.referenceSample)} />
              <F label="Ngày kiểm" value={formatDate(d.checkTime)} />
              <F label="Người kiểm" value={d.inspector?.fullName || d.inspector?.email || "Chưa phân công"} />
            </div>
            <div className="border-t border-slate-100 pt-3">
              <QcSamplingSummary
                lotSize={d.lotSize ?? d.qtyChecked}
                mode={d.inspectionMode}
                level={d.aqlLevel}
                aqlMajor={d.aqlStandard}
                aqlMinor={d.aqlMinor}
                sampleSize={d.sampleSize}
                acMajor={d.acValue}
                reMajor={d.reValue}
                acMinor={d.acMinor}
                reMinor={d.reMinor}
              />
            </div>
          </div>

          {/* Chỉ tiêu kiểm tra (bản chụp trên phiếu) */}
          <CriteriaTable
            items={checklistItems}
            readOnly
            emptyText="Phiếu chưa ghi chỉ tiêu."
            toolbar={
              d.template?.name ? (
                <Badge variant="outline" className="text-[11px] bg-indigo-50 text-indigo-700 border-indigo-200">
                  Mẫu kiểm tra: {d.template.name}
                </Badge>
              ) : null
            }
          />

          {/* Phiếu lập trước đợt 2: ô đo cứng cũ */}
          {!d.inspectionMode && (
            <LegacyMeasurements
              rows={[
                [
                  "D × R × C",
                  d.actualLength || d.actualWidth || d.actualHeight
                    ? `${d.actualLength ?? "—"} × ${d.actualWidth ?? "—"} × ${d.actualHeight ?? "—"} mm`
                    : null,
                ],
                ["Độ ẩm", d.moisture !== null && d.moisture !== undefined ? `${d.moisture} %` : null],
                ["Mã vạch", legacyBarcodeLabel(d.barcodeGrade)],
                ["Dựng hộp & mép keo", legacyTestLabel(d.fittingTestResult)],
                ["Thả rơi", legacyTestLabel(d.dropTestResult)],
              ]}
            />
          )}

          {d.printingIpqcSummary && (
            <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-2.5 text-xs flex flex-wrap items-center justify-between gap-2">
              <span className="text-slate-700">
                <b className="text-blue-900">Màu in (IPQC):</b>{" "}
                {d.printingIpqcSummary.visualColorMatch === "FAILED" ? "Không đạt" : "Đạt"} · L*a*b* thực tế{" "}
                <span className="font-mono">
                  {d.printingIpqcSummary.labActL ?? "—"} / {d.printingIpqcSummary.labActA ?? "—"} / {d.printingIpqcSummary.labActB ?? "—"}
                </span>
              </span>
              <Badge variant="outline" className="bg-white text-blue-800 border-blue-300 font-mono font-bold text-xs">
                ΔE = {d.printingIpqcSummary.deltaE ?? "—"}
              </Badge>
            </div>
          )}

          {/* Lỗi phát hiện */}
          {normalizeDefectTallyItems(d.defectDetails).length > 0 && (
            <StageDefectSelectorTable
              processStage="FQC"
              lotSize={d.lotSize || d.qtyChecked || 0}
              sampleSize={d.sampleSize || 0}
              defectDetails={normalizeDefectTallyItems(d.defectDetails)}
              onChange={() => {}}
              readOnly
              hideSampleSummary
            />
          )}

          {/* Kết luận */}
          <VerdictSummary
            status={d.status}
            counts={{ critical: d.criticalDefects || 0, major: d.majorDefects || 0, minor: d.minorDefects || 0 }}
            sampleSize={d.sampleSize}
            reMajor={d.reValue}
            reMinor={d.reMinor}
            overrideReason={d.overrideReason}
            criteria={checklistItems}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-2 border-t border-slate-100 pt-2">
              <F label="SL nhập kho" value={formatNumber(d.qtyPassed)} hl />
              <F label="SL loại" value={d.qtyFailed ? formatNumber(d.qtyFailed) : "0"} />
              <F label="Số CoA" value={d.coaNo || "—"} mono />
              <F label="Nhận xét" value={d.notes || "—"} />
            </div>
          </VerdictSummary>

          {/* Dispatch & Delivery Status Card */}
          <div className="border rounded-lg bg-white shadow-2xs p-4 space-y-3">
            <div className="flex items-center justify-between border-b border-slate-100 pb-2">
              <div className="flex items-center gap-2">
                <Truck className="w-4 h-4 text-slate-700" />
                <span className="font-semibold text-slate-900 text-sm">
                  Thông Tin Điều Vận &amp; Giao Hàng
                </span>
              </div>
              {d.deliveryInfo?.hasDelivery ? (
                <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 font-semibold text-xs">
                  ĐÃ TẠO PHIẾU GIAO HÀNG
                </Badge>
              ) : d.deliveryInfo?.isReadyForDispatch ? (
                <Badge className="bg-sky-100 text-sky-800 border-sky-300 font-semibold text-xs">
                  ĐANG CHỜ GOM XE
                </Badge>
              ) : (
                <Badge className="bg-amber-100 text-amber-800 border-amber-300 font-semibold text-xs">
                  CHƯA TẠO PHIẾU GIAO
                </Badge>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <div className="p-2.5 bg-slate-50 rounded border border-slate-200">
                <span className="text-slate-500 block mb-0.5">SL Nghiệm Thu Đạt</span>
                <span className="font-bold text-slate-900 text-sm font-mono">
                  {formatNumber(d.qtyPassed || d.deliveryInfo?.qtyRemaining || 0)}
                </span>
              </div>
              <div className="p-2.5 bg-slate-50 rounded border border-slate-200">
                <span className="text-slate-500 block mb-0.5">Đã Lên Phiếu Giao</span>
                <span className="font-bold text-emerald-600 text-sm font-mono">
                  {formatNumber(d.deliveryInfo?.deliveredQty || 0)}
                </span>
              </div>
              <div className="p-2.5 bg-slate-50 rounded border border-slate-200">
                <span className="text-slate-500 block mb-0.5">SL Còn Lại Chưa Giao</span>
                <span className="font-bold text-slate-900 text-sm font-mono">
                  {formatNumber(d.deliveryInfo?.qtyRemaining || 0)}
                </span>
              </div>
              <div className="p-2.5 bg-slate-50 rounded border border-slate-200">
                <span className="text-slate-500 block mb-0.5">Tem Thùng Carton</span>
                <span className="font-bold text-slate-900 text-sm font-mono">
                  {d.deliveryInfo?.cartonStats?.available || 0} / {d.deliveryInfo?.cartonStats?.total || 0} sẵn sàng
                </span>
              </div>
            </div>

            {d.deliveryInfo?.latestDelivery && (
              <div className="p-3 bg-emerald-50/50 rounded border border-emerald-200 flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span className="text-slate-700">
                    Phiếu xuất giao gần nhất:{" "}
                    <span className="font-mono font-bold text-emerald-800">
                      {d.deliveryInfo.latestDelivery.deliveryId}
                    </span>
                  </span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs border-emerald-300 text-emerald-800 hover:bg-emerald-100"
                  onClick={() =>
                    navigate({
                      to: "/delivery/$deliveryId",
                      params: { deliveryId: d.deliveryInfo.latestDelivery.deliveryId || d.deliveryInfo.latestDelivery.id },
                    })
                  }
                >
                  Xem Phiếu Giao
                </Button>
              </div>
            )}
          </div>

          {/* Phiếu đóng gói (bộ phận Sản xuất) — KCS chỉ xem */}
          <div className="border rounded-lg bg-white shadow-2xs p-4 space-y-3" data-testid="fqc-packing-slip-card">
            <div className="flex items-center justify-between border-b pb-2 gap-2 flex-wrap">
              <h3 className="text-xs font-black uppercase tracking-wider text-amber-800 flex items-center gap-2">
                <Box className="w-4 h-4 text-amber-600" /> Phiếu đóng gói
              </h3>
              {d.packingSlip ? (
                d.packingSlip.status === "PACKED" ? (
                  <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 text-xs flex items-center gap-1">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Đã đóng gói &amp; nhập kho
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-amber-800 bg-amber-50 border-amber-300 text-xs">Chờ đóng gói</Badge>
                )
              ) : (
                <Badge variant="outline" className="text-slate-500 bg-slate-50 text-xs">Chưa bàn giao (lô chưa Đạt)</Badge>
              )}
            </div>
            {d.packingSlip ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                <div>
                  <span className="text-slate-500 block">Mã phiếu</span>
                  <span className="font-mono font-bold text-indigo-700">{d.packingSlip.code}</span>
                </div>
                <div>
                  <span className="text-slate-500 block">Người đóng gói</span>
                  <span className="font-semibold text-slate-900">{d.packingSlip.packer?.fullName || "—"}</span>
                </div>
                <div>
                  <span className="text-slate-500 block">SL nhận từ KCS / đạt / lỗi</span>
                  <span className="font-mono font-bold">
                    {formatNumber(d.packingSlip.qtyFromQc || 0)} /{" "}
                    <span className="text-emerald-700">{d.packingSlip.qtyPacked != null ? formatNumber(d.packingSlip.qtyPacked) : "—"}</span> /{" "}
                    <span className="text-rose-600">{formatNumber(d.packingSlip.qtyDefect || 0)}</span>
                  </span>
                </div>
                <div>
                  <span className="text-slate-500 block">Quy cách</span>
                  <span className="font-mono">
                    {d.packingSlip.pcsPerBox ? `${formatNumber(d.packingSlip.pcsPerBox)} cái/thùng · ${formatNumber(d.packingSlip.totalBoxes || 0)} thùng` : "—"}
                    {d.packingSlip.totalPallets ? ` · ${formatNumber(d.packingSlip.totalPallets)} pallet` : ""}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500 block">Số lô TP</span>
                  <span className="font-mono font-bold text-blue-900">{d.packingSlip.lotNumber || "—"}</span>
                </div>
                <div>
                  <span className="text-slate-500 block">Phiếu nhập kho</span>
                  {d.packingSlip.receiptDocNo ? (
                    <EntityLink
                      entityType="warehouseDocument"
                      id={d.packingSlip.receiptDocId}
                      code={d.packingSlip.receiptDocNo}
                      variant="link"
                      className="font-mono font-bold text-emerald-700"
                    />
                  ) : (
                    <span className="text-slate-400 italic">Chưa nhập kho</span>
                  )}
                </div>
                <div>
                  <span className="text-slate-500 block">Kho nhận</span>
                  <span className="font-semibold">{d.packingSlip.targetWarehouse?.name || "—"}</span>
                </div>
                <div>
                  <span className="text-slate-500 block">Tem thùng</span>
                  <span className="font-mono">{formatNumber(d.packingSlip.labels?.length || 0)} tem</span>
                </div>
              </div>
            ) : null}
          </div>

          {/* Categorized Media Evidence (Sample / Defect / CoA) */}
          {normalizeCategorizedAttachments(d.attachments, "QC").length > 0 && (
            <QcCategorizedMediaSection
              mode="QC"
              attachments={d.attachments}
              readOnly
            />
          )}

          {/* Universal 3-Tab Context Panel - đã render bởi StandardDetailLayout */}
        </div>
      </StandardDetailLayout>



      <ConfirmDialog
        open={showDelete}
        onOpenChange={setShowDelete}
        title={`Xoá QC cuối ${d.finalQcId}?`}
        description="Hành động này không thể hoàn tác."
        onConfirm={handleDelete}
        isLoading={isDeleting}
        variant="destructive"
      />

      <PrintDialog
        open={showFqcPrint}
        onClose={() => setShowFqcPrint(false)}
        type="qc_final"
        data={d}
      />

      <PrintDialog
        open={showCoaPrint}
        onClose={() => setShowCoaPrint(false)}
        type="coa"
        data={d}
      />

    </>
  );
}
